"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _useFormElementValidation = _interopRequireDefault(require("../../hooks/useFormElementValidation"));
var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));
var _useLabel = _interopRequireDefault(require("../../hooks/useLabel"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
// import '@sm360/ckeditor-config/build/translations/fr.js'
/**
 * The Editor component...
 *
 * This component is an opinionated wrapper around...
 */
const Editor = props => {
  const {
    id: controlledId,
    name,
    value: controlledValue,
    label: initialLabel,
    helper,
    maxChar,
    readOnly,
    disabled,
    required,
    locale,
    addon,
    addonPosition,
    onChange,
    onError,
    onEditorReady,
    className,
    config,
    token,
    uploadUrl
  } = props;
  const id = _react.default.useRef((0, _useRandomId.default)(controlledId)).current;
  const editorRef = _react.default.useRef(null);
  const textareaRef = _react.default.useRef(id || name);
  const [value, setValue] = _react.default.useState(controlledValue || '');
  const [error, setError] = _react.default.useState(false);
  const [isReadOnly, setIsReadOnly] = _react.default.useState(readOnly || disabled || false);
  const [errors] = (0, _useFormElementValidation.default)(value, {
    required,
    maxChar
  });
  const {
    label
  } = (0, _useLabel.default)(initialLabel, {
    ...props,
    id,
    value: value
  });
  const [editorLoaded, setEditorLoaded] = _react.default.useState(false);
  const {
    CKEditor,
    ClassicEditor
  } = editorRef.current || {};
  _react.default.useEffect(() => {
    editorRef.current = {
      CKEditor: require('@ckeditor/ckeditor5-react').CKEditor,
      ClassicEditor: require('@sm360/ckeditor-config')
    };
    editorRef.current.ClassicEditor.defaultConfig.language = locale;
    if (uploadUrl && token) {
      editorRef.current.ClassicEditor.defaultConfig.simpleUpload = {
        ...editorRef.current.ClassicEditor.defaultConfig.simpleUpload,
        uploadUrl,
        headers: {
          Authorization: "Bearer " + token
        }
      };
    }
    if (config === 'simple') {
      editorRef.current.ClassicEditor.defaultConfig.toolbar = {
        items: ['bulletedList', 'numberedList', '|', 'undo', 'redo'],
        shouldNotGroupWhenFull: true
      };
    } else {
      editorRef.current.ClassicEditor.defaultConfig.toolbar = {
        items: ['undo', 'redo', '|', 'link', '|', 'imageUpload', 'horizontalLine', 'specialCharacters', '|', 'removeFormat', '|', 'wproofreader', '|', 'fullscreen', '|', 'sourceEditing', '-', 'bold', 'italic', 'strikethrough', '|', 'alignment', '|', 'numberedList', 'bulletedList', '|', 'outdent', 'indent', '|', 'blockQuote', '|', 'style', '|', 'heading', '|', 'fontSize', 'fontFamily', 'fontColor'],
        shouldNotGroupWhenFull: true
      };
    }
    setEditorLoaded(true);
  }, [config, token, uploadUrl, locale]);
  _react.default.useEffect(() => {
    setError(errors.length > 0);
  }, [errors]);
  _react.default.useEffect(() => {
    setIsReadOnly(readOnly || disabled || false);
  }, [readOnly, disabled]);
  _react.default.useEffect(() => {
    setError(errors.length > 0);
    if (typeof onError === 'function') {
      onError({
        name: textareaRef.current.name,
        errors: errors.length ? errors : []
      });
    }
  }, [errors, onError]);
  const handleChange = v => {
    setValue(v);
    if (typeof onChange === 'function') {
      onChange(v, textareaRef.current.name);
    }
  };
  const inputAddon = /*#__PURE__*/_react.default.isValidElement(addon) ? addon : /*#__PURE__*/_react.default.createElement('span', {}, addon);
  const containerClasses = (0, _clsx.default)('uk-form-item', 'uk-textarea_ uk-editor', disabled && 'disabled', addon && 'has-info', addon && "has-info--" + addonPosition, (helper && helper.length > 0 || error) && 'has-helper', error && 'has-error', className);
  return editorLoaded ? /*#__PURE__*/_react.default.createElement("div", {
    className: containerClasses
  }, label, /*#__PURE__*/_react.default.createElement("div", {
    className: "flex-container"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-inputtable"
  }, /*#__PURE__*/_react.default.createElement(CKEditor, {
    editor: ClassicEditor,
    data: controlledValue,
    onReady: editor => {
      editor.language = locale;
      if (typeof onEditorReady === 'function') onEditorReady(editor);
    },
    onChange: (event, editor) => {
      const data = editor.getData();
      handleChange(data);
    },
    onBlur: (event, editor) => {
      // console.log( 'Blur.', editor );
    },
    onFocus: (event, editor) => {
      // console.log( 'Focus.', editor );
    },
    disabled: isReadOnly,
    config: {
      language: locale,
      removePlugins: uploadUrl && token ? [] : ['ImageUpload']
    }
  })), maxChar && /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-form-item__helper uk-form-item__helper--counter"
  }, "Characters left: ", maxChar - value.length > 0 ? maxChar - value.length : 0), addon && /*#__PURE__*/_react.default.createElement("div", {
    className: (0, _clsx.default)('uk-form-item__addon', addonPosition)
  }, inputAddon)), (helper && helper.length > 0 || error) && /*#__PURE__*/_react.default.createElement("small", {
    className: "uk-form-item__helper"
  }, error ? errors : helper)) : null;
};
Editor.propTypes = {
  /**
   * ID of the Editor (used to bind the label to the Editor)
   * NB: the ID will be randomly generated if not set.
   */
  id: _propTypes.default.string,
  /**
   * Name of the Editor (used instead of the ID when submitted)
   */
  name: _propTypes.default.string,
  /**
   * Default value of the Editor
   */
  value: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number]),
  /**
   * Label of the Editor
   */
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Editor placeholder
   */
  placeholder: _propTypes.default.string,
  /**
   * Helper text, is replaced by a message in case of error
   */
  helper: _propTypes.default.string,
  /**
   * Remove the placeholder (if any), replace it with a floating label
   */
  floatLabel: _propTypes.default.bool,
  /**
   * Enable the resizing of the Editor
   */
  resizable: _propTypes.default.bool,
  /**
   * Set and display a maximun of characters the user can input before triggering an error
   */
  maxChar: _propTypes.default.number,
  /**
   * Set the Editor as read-only
   */
  readOnly: _propTypes.default.bool,
  /**
   * Set the Editor as disabled
   */
  disabled: _propTypes.default.bool,
  /**
   * Form-validation: set the Editor as required
   */
  required: _propTypes.default.bool,
  /**
   * Add an informative box next to the input element
   */
  addon: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Position the info relative to the input element
   */
  addonPosition: _propTypes.default.oneOf(['left', 'right']),
  /**
   * Callback function onChange
   */
  onChange: _propTypes.default.func,
  /**
   * Callback function onInput
   */
  onInput: _propTypes.default.func,
  /**
   * Callback function onBlur
   */
  onBlur: _propTypes.default.func,
  /**
   * Callback function onError
   */
  onError: _propTypes.default.func,
  /**
   * Callback function for retrieving the main editor instance
   */
  onEditorReady: _propTypes.default.func,
  config: _propTypes.default.string
};
Editor.defaultProps = {
  floatLabel: false,
  resizable: false,
  readOnly: false,
  disabled: false,
  required: false,
  addonPosition: 'left',
  config: 'simple'
};
var _default = exports.default = Editor;